import React, { useEffect, useState, useCallback } from 'react';
import { Shield, CheckCircle, XCircle, Clock, AlertTriangle, RefreshCw, Eye, X, FileText, User as UserIcon } from 'lucide-react';
import { toast } from 'sonner';
import { apiCall, API_URL } from '../../utils/api';
import { SecureFileLink } from '../hris/SecureFileLink';

/**
 * AdminProfileVerificationPanel
 * -----------------------------
 * Shows every user with verification_status in {under_review, rejected}.
 * Lets a super admin or admin view the submission and approve / reject with a
 * reason. Used inside AdminDashboard as a dedicated tab so it does not disturb
 * the legacy `is_admin_verified` flow.
 */
export default function AdminProfileVerificationPanel({ workersOnly = false }) {
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState([]);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState('under_review');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiCall('/api/admin/verification/pending');
      setPending(res.pending || []);
    } catch (e) {
      toast.error(e?.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = pending.filter(p =>
    filter === 'all' ? true : p.verification_status === filter
  );

  const counts = {
    under_review: pending.filter(p => p.verification_status === 'under_review').length,
    rejected: pending.filter(p => p.verification_status === 'rejected').length,
    all: pending.length,
  };

  return (
    <div className="space-y-5">
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-6 text-white">
        <div className="flex items-center gap-3">
          <Shield className="w-8 h-8" />
          <div>
            <h2 className="text-2xl font-bold">Profile Verification</h2>
            <p className="text-white/90 text-sm mt-1">
              {workersOnly ? 'Review workers in your organisation who have submitted their verification profile.' : 'Review workers, employers, and agents who have submitted their verification profile.'}
            </p>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {[
          { id: 'under_review', label: 'Awaiting review', count: counts.under_review },
          { id: 'rejected', label: 'Rejected', count: counts.rejected },
          { id: 'all', label: 'All', count: counts.all },
        ].map(f => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition ${
              filter === f.id
                ? 'bg-blue-600 text-white shadow'
                : 'bg-white text-slate-700 border border-slate-200 hover:bg-slate-50'
            }`}
            data-testid={`pv-filter-${f.id}`}
          >
            {f.label} ({f.count})
          </button>
        ))}
        <button
          onClick={load}
          className="ml-auto px-3 py-1.5 text-sm text-slate-600 hover:text-slate-900 flex items-center gap-1"
          data-testid="pv-refresh"
        >
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-500">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center">
          <CheckCircle className="w-10 h-10 text-green-500 mx-auto mb-3" />
          <p className="text-slate-700 font-medium">All caught up — nothing to review</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(u => (
            <div
              key={u._id}
              className="bg-white border border-slate-200 rounded-2xl p-4 flex items-center gap-4 hover:shadow-md transition"
              data-testid={`pv-row-${u._id}`}
            >
              <div className={`p-3 rounded-xl ${badgeBg(u.verification_status)}`}>
                {iconFor(u.verification_status)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-bold text-slate-900 truncate">{u.full_name || u.email}</p>
                  <span className="text-xs font-medium bg-slate-100 text-slate-700 px-2 py-0.5 rounded-full">
                    {u.role}
                  </span>
                  <StatusPill status={u.verification_status} />
                </div>
                <p className="text-sm text-slate-600 mt-0.5">{u.email}</p>
                {u.verification_status === 'rejected' && u.rejection_reason && (
                  <p className="text-xs text-red-700 mt-1">Reason: {u.rejection_reason}</p>
                )}
              </div>
              <button
                onClick={() => setSelected(u._id)}
                className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg flex items-center gap-1"
                data-testid={`pv-review-btn-${u._id}`}
              >
                <Eye className="w-4 h-4" /> Review
              </button>
            </div>
          ))}
        </div>
      )}

      {selected && (
        <ReviewDrawer
          userId={selected}
          onClose={() => setSelected(null)}
          onDone={() => { setSelected(null); load(); }}
        />
      )}
    </div>
  );
}

function badgeBg(s) {
  if (s === 'under_review') return 'bg-amber-100 text-amber-700';
  if (s === 'rejected') return 'bg-red-100 text-red-700';
  return 'bg-slate-100 text-slate-700';
}
function iconFor(s) {
  if (s === 'under_review') return <Clock className="w-5 h-5" />;
  if (s === 'rejected') return <AlertTriangle className="w-5 h-5" />;
  return <UserIcon className="w-5 h-5" />;
}
function StatusPill({ status }) {
  const map = {
    under_review: ['bg-amber-100 text-amber-800', 'Awaiting review'],
    rejected: ['bg-red-100 text-red-800', 'Rejected'],
    approved: ['bg-green-100 text-green-800', 'Approved'],
    profile_incomplete: ['bg-slate-100 text-slate-700', 'Incomplete'],
  };
  const [cls, label] = map[status] || ['bg-slate-100 text-slate-700', status];
  return <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${cls}`}>{label}</span>;
}

// =============================================================
// Review Drawer
// =============================================================
function ReviewDrawer({ userId, onClose, onDone }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showReject, setShowReject] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await apiCall(`/api/admin/verification/${userId}`);
        setData(res);
      } catch (e) {
        toast.error(e?.message || 'Failed to load submission');
      } finally {
        setLoading(false);
      }
    })();
  }, [userId]);

  const approve = async () => {
    setBusy(true);
    try {
      await apiCall(`/api/admin/verification/${userId}/approve`, { method: 'POST' });
      toast.success('User approved — they can now use the app');
      onDone();
    } catch (e) {
      toast.error(e?.message || 'Approve failed');
    } finally {
      setBusy(false);
    }
  };

  const reject = async () => {
    if (reason.trim().length < 5) {
      toast.error('Please provide a clear reason (min 5 characters)');
      return;
    }
    setBusy(true);
    try {
      await apiCall(`/api/admin/verification/${userId}/reject`, {
        method: 'POST',
        body: JSON.stringify({ reason: reason.trim() }),
      });
      toast.success('User rejected — they have been notified by email');
      onDone();
    } catch (e) {
      toast.error(e?.message || 'Reject failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex justify-end" data-testid="pv-drawer">
      <div className="w-full max-w-2xl bg-white h-full overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-white border-b border-slate-200 p-4 flex items-center justify-between">
          <h2 className="font-bold text-slate-900">Review verification submission</h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="pv-drawer-close">
            <X className="w-5 h-5 text-slate-600" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {loading ? (
            <p className="text-slate-500">Loading…</p>
          ) : (
            <>
              <section>
                <h3 className="font-bold text-slate-900 mb-2">User</h3>
                <KV k="Name" v={data?.user?.full_name} />
                <KV k="Email" v={data?.user?.email} />
                <KV k="Phone" v={data?.user?.phone} />
                <KV k="Role" v={data?.user?.role} />
                <KV k="Status" v={<StatusPill status={data?.user?.verification_status} />} />
                {data?.user?.rejection_reason && (
                  <KV k="Last rejection" v={<span className="text-red-700">{data.user.rejection_reason}</span>} />
                )}
              </section>

              <section>
                <h3 className="font-bold text-slate-900 mb-2">Submitted profile</h3>
                {!data?.record ? (
                  <p className="text-sm text-slate-500">No submission yet.</p>
                ) : (
                  <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-1.5">
                    {Object.entries(data.record).map(([k, v]) => {
                      if (['_id','user_id','role','created_at','updated_at','submitted_at','reviewed_at','reviewed_by','status'].includes(k)) return null;
                      // Document fields render with a "View" link
                      if (v && typeof v === 'object' && v.url) {
                        return (
                          <KV key={k} k={prettyField(k)} v={
                            <SecureFileLink path={v.url} workerId={data.record.user_id} sensitivity={data.record.role === 'worker' ? 'restricted' : 'high'}
                              fileLabel={`${prettyField(k)} (${v.filename || 'file'})`} className="text-blue-600 hover:underline inline-flex items-center gap-1 text-sm" testId={`pv-doc-${k}`}>
                              <FileText className="w-4 h-4" /> View ({v.filename || 'file'})
                            </SecureFileLink>
                          } />
                        );
                      }
                      // Hide raw ssn_hash/last4 — show last4 only
                      if (k === 'ssn_hash') return null;
                      if (k === 'ssn_last4') return <KV key={k} k="SSN (last 4)" v={`••• ${v}`} />;
                      if (k === 'ssn') return <KV key={k} k="SSN" v={v ? 'On file (hashed)' : 'Not provided'} />;
                      return <KV key={k} k={prettyField(k)} v={String(v ?? '—')} />;
                    })}
                  </div>
                )}
              </section>

              {!showReject ? (
                <div className="flex gap-2 sticky bottom-0 bg-white border-t border-slate-200 -mx-5 px-5 py-4">
                  <button
                    onClick={() => setShowReject(true)}
                    className="flex-1 px-4 py-2.5 bg-red-50 text-red-700 border border-red-200 hover:bg-red-100 rounded-xl font-medium flex items-center justify-center gap-1"
                    data-testid="pv-drawer-reject-show"
                  >
                    <XCircle className="w-5 h-5" /> Reject…
                  </button>
                  <button
                    onClick={approve}
                    disabled={busy}
                    className="flex-1 px-4 py-2.5 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl font-semibold shadow-md hover:opacity-90 flex items-center justify-center gap-1 disabled:opacity-50"
                    data-testid="pv-drawer-approve"
                  >
                    <CheckCircle className="w-5 h-5" /> Approve
                  </button>
                </div>
              ) : (
                <div className="sticky bottom-0 bg-white border-t border-slate-200 -mx-5 px-5 py-4">
                  <label className="block text-sm font-medium text-slate-700 mb-1">Reason (visible to the user, min 5 chars)</label>
                  <textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                    placeholder="e.g., Government ID is blurry — please re-upload a clearer photo."
                    data-testid="pv-drawer-reject-reason"
                  />
                  <div className="flex gap-2 mt-3">
                    <button
                      onClick={() => { setShowReject(false); setReason(''); }}
                      className="flex-1 px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-medium"
                      data-testid="pv-drawer-reject-cancel"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={reject}
                      disabled={busy}
                      className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl font-semibold disabled:opacity-50"
                      data-testid="pv-drawer-reject-confirm"
                    >
                      {busy ? 'Rejecting…' : 'Confirm rejection'}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function KV({ k, v }) {
  return (
    <div className="flex gap-2 py-0.5">
      <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide min-w-[140px]">{k}</span>
      <span className="text-sm text-slate-800">{v ?? '—'}</span>
    </div>
  );
}

function prettyField(k) {
  const map = {
    full_name: 'Full legal name',
    phone: 'Phone',
    address: 'Address',
    date_of_birth: 'Date of birth',
    bank_account_holder_name: 'Bank holder name',
    bank_account_last4: 'Bank account number',
    bank_routing_last4: 'Routing number',
    company_name: 'Company name',
    business_type: 'Business type',
    industry: 'Industry',
    tax_id: 'Tax ID / EIN',
    business_registration_number: 'Business reg. #',
    description: 'Description',
    government_id_document: 'Government ID',
    selfie_document: 'Selfie',
    right_to_work_document: 'Right-to-work doc',
    business_license_document: 'Business license',
  };
  return map[k] || k.replace(/_/g, ' ');
}
